import React, { useMemo } from 'react';
import Ionicons from '@expo/vector-icons/Ionicons';
import { Modal, Platform, ScrollView, StyleSheet, Text, TouchableOpacity, View } from './rn';
import GlassControlButton from './GlassControlButton';
import { fontSize, radius, spacing } from '../theme';
import { useTheme } from '../theme/ThemeContext';
import { useTranslation } from '../i18n/I18nContext';

// 7.14 / D-704 — Apple'ın sayfa (sheet) kuralı, 24 Mart 2026 güncellemesi:
// - Kapat/İptal ÜSTTE ve SOLDA; "Bitti" varsa ÜSTTE ve SAĞDA. Önceden bizim
//   sayfalarımız "Bitti"yi en altta tutuyordu.
// - Kapat, yazı değil standart "X" sembolü ("never use the text Close").
// - Aşağı kaydırınca kapanır. iPhone'da bu yüzden gerçek sistem sayfası
//   (`pageSheet`) kullanılıyor: kaydırarak kapatma, cam görünümü ve geçiş
//   sistemden gelir. Android ve web'de aynı üst çubukla alttan açılan bir kart.
// Uygulamanın yeni sayfaları bu bileşenle yapılır; eskiler sırayla taşınır.

type Done = { label: string; onPress: () => void; disabled?: boolean; testID?: string };

type Props = {
  visible: boolean;
  title: string;
  onClose: () => void;
  /** Görevi bitiren eylem; varsa sağ üstte, vurgulu. */
  done?: Done;
  /** Başlığın yanına gelen küçük öğe (ör. ⓘ). */
  titleAccessory?: React.ReactNode;
  children: React.ReactNode;
  testID?: string;
};

export default function AppSheet({ visible, title, onClose, done, titleAccessory, children, testID }: Props) {
  const colors = useTheme();
  const { t } = useTranslation();
  const styles = useMemo(() => makeStyles(colors), [colors]);
  const native = Platform.OS === 'ios';

  const bar = (
    <View style={styles.bar}>
      <GlassControlButton testID={testID ? `${testID}-close` : undefined} onPress={onClose} accessibilityLabel={t('common.close')}>
        <Ionicons name="close" size={22} color={colors.black} />
      </GlassControlButton>
      <View style={styles.titleWrap}>
        <Text style={styles.title} numberOfLines={1} accessibilityRole="header">{title}</Text>
        {titleAccessory}
      </View>
      {done ? (
        <TouchableOpacity
          testID={done.testID}
          onPress={done.onPress}
          disabled={done.disabled}
          accessibilityRole="button"
          accessibilityState={{ disabled: !!done.disabled }}
          style={[styles.done, done.disabled && styles.doneDisabled]}>
          <Text style={styles.doneText}>{done.label}</Text>
        </TouchableOpacity>
      ) : (
        <View style={styles.barSpacer} />
      )}
    </View>
  );

  if (native) {
    return (
      <Modal visible={visible} animationType="slide" presentationStyle="pageSheet" onRequestClose={onClose}>
        <View style={styles.nativeSheet} testID={testID}>
          {bar}
          <ScrollView contentContainerStyle={styles.content}>{children}</ScrollView>
        </View>
      </Modal>
    );
  }
  return (
    <Modal visible={visible} animationType="slide" transparent onRequestClose={onClose}>
      <View style={styles.backdrop}>
        <View style={styles.sheet} testID={testID}>
          {bar}
          <ScrollView contentContainerStyle={styles.content}>{children}</ScrollView>
        </View>
      </View>
    </Modal>
  );
}

function makeStyles(colors: ReturnType<typeof useTheme>) {
  return StyleSheet.create({
    nativeSheet: { flex: 1, backgroundColor: colors.white },
    backdrop: { flex: 1, backgroundColor: 'rgba(0,0,0,0.4)', justifyContent: 'flex-end' },
    sheet: {
      backgroundColor: colors.white,
      borderTopLeftRadius: radius.layerOuter,
      borderTopRightRadius: radius.layerOuter,
      maxHeight: '92%',
    },
    bar: {
      flexDirection: 'row',
      alignItems: 'center',
      paddingHorizontal: spacing.md,
      paddingTop: spacing.md,
      paddingBottom: spacing.sm,
      gap: spacing.sm,
    },
    titleWrap: { flex: 1, flexDirection: 'row', alignItems: 'center', justifyContent: 'center', minWidth: 0 },
    title: { fontSize: fontSize.md, fontWeight: '600', color: colors.black, flexShrink: 1 },
    // Kapat düğmesiyle aynı genişlik: başlık ortada kalsın.
    barSpacer: { width: 40 },
    done: {
      minWidth: 40,
      minHeight: 40,
      paddingHorizontal: spacing.md,
      borderRadius: 20,
      backgroundColor: colors.black,
      alignItems: 'center',
      justifyContent: 'center',
    },
    doneDisabled: { opacity: 0.4 },
    doneText: { color: colors.white, fontWeight: '600', fontSize: fontSize.md },
    content: { padding: spacing.lg, gap: spacing.md },
  });
}
